import { Injectable, computed, signal } from '@angular/core';
import { ParamMap } from '@angular/router';
import { formatCityStateZip, formatStreet } from '../../property/address.service';
import { COUNTY_COPY, County, countyForJurisdiction } from '../../county/county-config';

/** Address as handed off from the landing page's query string; keys match the params and the API's address DTO. */
export interface SignupAddress {
  street: string;
  unit?: string;
  city: string;
  state: string;
  zip: string;
}

/**
 * County match for an address: `AddressLookupResponseDto` from `POST /customer-intake/lookup`. Local until
 * it's published in `@kin/property-tax-types`.
 */
export interface SignupAddressLookup {
  /** True when the address is in a county we serve. */
  activeCounty: boolean;
  /** e.g. `HARRIS_TX`, or null when nothing matched. */
  jurisdictionCode: string | null;
  parcelId: string | null;
  /** Estimated average annual neighborhood savings, or null when none are on file. */
  avgSavings: number | null;
  /** Owner of record and legal description for the matched parcel; null when nothing matched. */
  ownerName: string | null;
  legalDescription: string | null;
  /**
   * Identifies the intake the lookup saved; `PUT /customer-intake` submits with it as a bearer token.
   * Expires after 24 hours.
   */
  token: string;
}

export type SignupLookupStatus = 'idle' | 'loading' | 'done' | 'error';

/** The county whose copy sign-up shows before an address matches a county it enrolls. */
export const DEFAULT_SIGNUP_COUNTY: County = 'harris';

const REQUIRED_PARAMS = ['street', 'city', 'state', 'zip'] as const;

/** True when every required address param (all but unit) is present and non-blank. */
export function hasRequiredAddressParams(params: ParamMap): boolean {
  return REQUIRED_PARAMS.every(key => !!params.get(key)?.trim());
}

// Mirrors the backend's CustomerIntakeAddressDto validation
const CITY_PATTERN = /^[A-Za-z\s'.-]+$/;
const STATE_PATTERN = /^[A-Z]{2}$/;
const ZIP_PATTERN = /^\d{5}(-\d{4})?$/;
const MAX_LENGTH = { street: 200, unit: 20, city: 100 };

/** True when the address matches the format `POST /customer-intake` accepts. */
export function isWellFormedAddress(a: SignupAddress): boolean {
  return a.street.length <= MAX_LENGTH.street
    && (a.unit ?? '').length <= MAX_LENGTH.unit
    && a.city.length <= MAX_LENGTH.city
    && CITY_PATTERN.test(a.city)
    && STATE_PATTERN.test(a.state)
    && ZIP_PATTERN.test(a.zip);
}

/**
 * The "Is this you?" answer on the property card. `none` can't enroll; the others pick the capacity Form 50-162
 * checks for the signer (see `IntakeOwnerRelationship`).
 */
export type OwnerRelationship = 'self' | 'co' | 'entity' | 'none';

/**
 * Holds the sign-up address consumed from `?street=&unit=&city=&state=&zip=`
 * (same hand-off pattern as kinpub-ui's PropertyStore `queryAddress`) and its lookup result.
 */
@Injectable({ providedIn: 'root' })
export class SignupAddressStore {
  readonly address = signal<SignupAddress | null>(null);
  readonly lookup = signal<SignupAddressLookup | null>(null);
  readonly lookupStatus = signal<SignupLookupStatus>('idle');
  /** Answered for the looked-up property, so a new address starts over unanswered (`null`). */
  readonly ownerRelationship = signal<OwnerRelationship | null>(null);

  readonly hasAddress = computed(() => this.address() !== null);
  readonly isLookingUp = computed(() => this.lookupStatus() === 'loading');

  /**
   * The matched county, when this app has its sign-up copy (an appraisal district); null otherwise. A county
   * the backend turns on for sign-up needs that copy in `COUNTY_COPY` before it can enroll.
   */
  readonly signupCounty = computed(() => {
    const county = countyForJurisdiction(this.lookup()?.jurisdictionCode);
    return county && COUNTY_COPY[county].appraisalDistrict ? county : null;
  });

  /**
   * Enrollment is offered for a match in a county turned on for sign-up (`activeCounty`) with savings on file
   * and sign-up copy; otherwise the waitlist.
   */
  readonly isServiceable = computed(() => {
    const lookup = this.lookup();
    return this.hasAddress()
      && !!this.signupCounty()
      && !!lookup?.activeCounty
      && !!lookup.avgSavings;
  });

  /** The county whose copy sign-up shows: the matched one, else the default. */
  readonly displayCounty = computed(() => this.signupCounty() ?? DEFAULT_SIGNUP_COUNTY);

  readonly appraisalDistrict = computed(() => COUNTY_COPY[this.displayCounty()].appraisalDistrict ?? '');

  /** e.g. "123 Main St, Unit 4, Houston, TX 77002" */
  readonly singleLineAddress = computed(() => {
    const a = this.address();
    return a ? `${formatStreet(a)}, ${formatCityStateZip(a)}` : '';
  });

  /**
   * Sets the address when all required params are present (unit is optional) and well formed;
   * otherwise clears it (→ waitlist).
   */
  setFromQueryParams(params: ParamMap): SignupAddress | null {
    const value = (key: string) => params.get(key)?.trim() ?? '';
    this.clearLookup();

    if (!hasRequiredAddressParams(params)) {
      this.address.set(null);
      return null;
    }

    const address: SignupAddress = {
      street: value('street'),
      unit: value('unit') || undefined,
      city: value('city'),
      state: value('state').toUpperCase(),
      zip: value('zip'),
    };
    if (!isWellFormedAddress(address)) {
      this.address.set(null);
      return null;
    }
    this.address.set(address);
    return address;
  }

  reset(): void {
    this.address.set(null);
    this.clearLookup();
  }

  private clearLookup(): void {
    this.lookup.set(null);
    this.lookupStatus.set('idle');
    this.ownerRelationship.set(null);
  }
}
